import { Grid } from "@mui/material";
import { FieldArrayWithId, useFieldArray, useFormContext } from "react-hook-form";
import { useState } from "react";
import { RhfControlledDataTable } from "../../../../Layout/RhfControlledTable/RhfControlledDataTable";
import { RhfControlledTableColumnDefinition } from "../../../../Layout/RhfControlledTable/RhfControlledTableColumnDefinition";
import { BidPack } from "../../BidPack";
import { BidPackDestination, defaultBidPackDestination } from "../../BidPackDestination";
import { useFetchDestinationTypeGsdbs } from "../../Hooks/useFetchDestinationTypeGsdbs";
import {
  LocationTypeForDestinationEnum,
  locationTypesAsString,
  locationTypesForDestination,
} from "../../LocationTypeForDestination";
import { TextFieldInput } from "../../../../Forms/TextFieldInput";
import { LocationGsdbSelect } from "../../../../Forms/LocationSelect/LocationGsdbSelect";
import { LocationTypeSelect } from "../../../../Forms/LocationSelect/LocationTypeSelect";
import { ResetFilters } from "../../../../Layout/RhfControlledTable/ResetFilters";
import { RhfControlledDataTableFilter } from "../../../../Layout/RhfControlledTable/RhfControlledDataTableFilter";
import { EnumSelect } from "../../../../Layout/Select/EnumSelect";
import { mapReactHookFormIndizesToGivenFieldArray } from "../../../../Utils/reactHookFormUtils";

export const BidPackDestinationForm = () => {
  const { control, setValue, watch } = useFormContext<BidPack>();
  const { fields, prepend, remove } = useFieldArray<BidPack>({
    control,
    name: "Destinations",
  });

  const [
    plantSelectionListItems,
    edcSelectionListItems,
    odcSelectionListItems,
    epcSelectionListItems,
  ] = useFetchDestinationTypeGsdbs();

  const associatedMenuItemsFor = (destinationTypeId: number) => {
    switch (destinationTypeId) {
      case LocationTypeForDestinationEnum.Plant:
        return plantSelectionListItems.data;
      case LocationTypeForDestinationEnum.EDC:
        return edcSelectionListItems.data;
      case LocationTypeForDestinationEnum.ODC:
        return odcSelectionListItems.data;
      case LocationTypeForDestinationEnum.EPC:
        return epcSelectionListItems.data;
      default:
        return [];
    }
  };

  const resetSelectedGsdb = (index: number) => {
    setValue(`Destinations.${index}.DestinationGsdb`, "");
  };

  const bidPackDestinationTableColumns: RhfControlledTableColumnDefinition<
    BidPack,
    "Destinations"
  >[] = [
    {
      fieldKey: "DestinationTypeId",
      isNumeric: false,
      headerText: "Type",
      width: "20%",
      render: (fieldName, disabled, index) => (
        <LocationTypeSelect
          className="HALLO WELT"
          control={control}
          name={fieldName}
          onLocationTypeChanged={() => resetSelectedGsdb(index)}
          locationTypes={locationTypesForDestination}
        />
      ),
    },
    {
      fieldKey: "DestinationGsdb",
      isNumeric: false,
      headerText: "GSDB *",
      width: "80%",
      render: (fieldName, disabled, index) => (
        <LocationGsdbSelect
          hideLabel
          control={control}
          name={fieldName}
          options={associatedMenuItemsFor(watch(`Destinations.${index}.DestinationTypeId`))}
        />
      ),
    },
  ];

  const [filteredFields, setFilteredFields] = useState<
    FieldArrayWithId<BidPack, "Destinations" | "OriginRegions">[] | undefined
  >();

  const [resetFilters, setResetFilters] = useState<boolean>(false);

  const filterBidPackDestinations = ({ filterValue }: { filterValue: number | string }) => {
    let filteredBidPackDestinations: FieldArrayWithId<BidPack, "Destinations" | "OriginRegions">[] =
      [];

    if (typeof filterValue === "number") {
      // Filter all fields for destinationType
      filteredBidPackDestinations = fields.filter((field) => {
        const bidPackDestinationField = field as BidPackDestination;
        return bidPackDestinationField.DestinationTypeId === filterValue;
      });
    }

    if (typeof filterValue === "string") {
      // Filter all fields for destination GSDB substring
      filteredBidPackDestinations = fields.filter((field) => {
        const bidPackDestinationField = field as BidPackDestination;
        return bidPackDestinationField.DestinationGsdb.startsWith(filterValue);
      });
    }

    // Create a new array with the React Hook form indices
    return mapReactHookFormIndizesToGivenFieldArray({
      fieldsFromFormContext: fields,
      filteredFields: filteredBidPackDestinations,
    });
  };

  return (
    <>
      <Grid item xs={12} container>
        <Grid item xs={2} pl="16px" pt="16px">
          <TextFieldInput
            control={control}
            name="DestinationName"
            label="Destination"
            ignoreFormDisabledState
          />
        </Grid>
      </Grid>

      <Grid container direction="row" pl={2} mt={1} spacing={0.5}>
        <Grid item>
          <EnumSelect
            optionsAsMap={locationTypesAsString}
            onSelectionChange={(destinationType: number) =>
              setFilteredFields(filterBidPackDestinations({ filterValue: destinationType }))
            }
            reset={resetFilters}
          />
        </Grid>

        <Grid item>
          <RhfControlledDataTableFilter
            placeholder="Destination GSDB"
            handleSearch={(destinationGsdb: string) =>
              setFilteredFields(filterBidPackDestinations({ filterValue: destinationGsdb }))
            }
            reset={resetFilters}
          />
        </Grid>

        <ResetFilters
          handleClick={() => {
            setFilteredFields(undefined);
            setResetFilters(!resetFilters);
          }}
        />
      </Grid>

      <Grid item xs={12} container direction="column" style={{ paddingTop: "4px" }}>
        <RhfControlledDataTable
          fieldArrayName="Destinations"
          columns={bidPackDestinationTableColumns}
          rows={filteredFields ?? fields}
          insertRow={prepend}
          removeRow={remove}
          defaultForNewRow={defaultBidPackDestination}
        />
      </Grid>
    </>
  );
};
